/**
 * Centralized score color theming for Comments features
 *
 * Provides a single source of truth for score-based colors used across Comments components
 * (CommentScoreMetric, MiniCommentScore, badges, charts, etc.)
 *
 * ## Color Properties
 *
 * The theme returns 5 color tokens for each score level:
 * - `bg`: Inner container/wrapper background (circle behind the number)
 * - `textColor`: Text and icon colors
 * - `border`: Border colors for containers
 * - `progressColor`: Progress arc stroke color (the main visible line)
 * - `progressBg`: Progress arc base background (behind the progress stroke)
 *
 * ## Color Variants
 *
 * **Vibrant (default)**
 * - LOW (0-20): Red
 * - MID_LOW (21-40): Orange
 * - MEDIUM (41-60): Yellow
 * - MID_HIGH (61-80): Green
 * - HIGH (81-100): Green
 *
 * **Neutral**
 * - LOW (0-20): Grey (more subtle, less alarming)
 * - All other levels: Same as vibrant
 *
 * ## Implementation
 *
 * All CSS variables are defined in `./commentScoreTheme.css`:
 * - Vibrant: `--score-{level}-bg`, `--score-{level}-text`, etc.
 * - Neutral: `--score-low-neutral-bg`, `--score-low-neutral-text`, etc.
 *
 * The variant parameter selects which variable set to return - vibrant uses base variables,
 * neutral overrides only the LOW level with `-neutral-` variables.
 *
 * ## Usage
 *
 * ```typescript
 * import { getScoreColors, getScoreLevel } from '../util/commentScoreTheme'
 *
 * // Get colors for a score (vibrant by default - red for low scores)
 * const colors = getScoreColors(45)
 * // colors.bg, colors.textColor, colors.border, colors.progressColor, colors.progressBg
 *
 * // Get neutral variant (grey for low scores)
 * const colors = getScoreColors(15, 'neutral')
 *
 * // Use in components
 * <div style={{ backgroundColor: colors.bg, color: colors.textColor }}>
 *   {score}
 * </div>
 *
 * // Get score level
 * const level = getScoreLevel(75) // Returns 'midHigh'
 * ```
 */

// Side-effect import: registers the --score-* CSS variables on :root so the
// var() references below resolve regardless of which page-level CSS module
// happens to be loaded first.
import './commentScoreTheme.css'

export const SCORE_LEVELS = {
  LOW: 'low',
  MID_LOW: 'midLow',
  MEDIUM: 'medium',
  MID_HIGH: 'midHigh',
  HIGH: 'high',
} as const

export type ScoreLevel = (typeof SCORE_LEVELS)[keyof typeof SCORE_LEVELS]

// Define the order of levels from lowest to highest
export const SCORE_LEVEL_ORDER = [
  SCORE_LEVELS.LOW,
  SCORE_LEVELS.MID_LOW,
  SCORE_LEVELS.MEDIUM,
  SCORE_LEVELS.MID_HIGH,
  SCORE_LEVELS.HIGH,
] as const

// Thresholds are defined as the maximum score for each level
export const SCORE_THRESHOLDS = {
  [SCORE_LEVELS.LOW]: 20, // 0-20: low
  [SCORE_LEVELS.MID_LOW]: 40, // 21-40: midLow
  [SCORE_LEVELS.MEDIUM]: 60, // 41-60: medium
  [SCORE_LEVELS.MID_HIGH]: 80, // 61-80: midHigh
  [SCORE_LEVELS.HIGH]: 100, // 81-100: high
} as const

type ScoreColorTheme = {
  bg: string
  textColor: string
  border: string
  progressColor: string
  progressBg: string
}

type Level = 'low' | 'mid-low' | 'medium' | 'mid-high' | 'high'

// Helper to create color theme for a level
const createColorVars = (level: Level): ScoreColorTheme => ({
  bg: `var(--score-${level}-bg)`,
  textColor: `var(--score-${level}-text)`,
  border: `var(--score-${level}-border)`,
  progressColor: `var(--score-${level}-progress)`,
  progressBg: `var(--score-${level}-progress-bg)`,
})

// Base color mappings shared by most levels
const BASE_COLORS: Record<ScoreLevel, ScoreColorTheme> = {
  [SCORE_LEVELS.LOW]: createColorVars('low'),
  [SCORE_LEVELS.MID_LOW]: createColorVars('mid-low'),
  [SCORE_LEVELS.MEDIUM]: createColorVars('medium'),
  [SCORE_LEVELS.MID_HIGH]: createColorVars('mid-high'),
  [SCORE_LEVELS.HIGH]: createColorVars('high'),
} as const

// Vibrant variant - uses base colors (red/orange for low scores)
export const SCORE_COLOR_VARS_VIBRANT: Record<ScoreLevel, ScoreColorTheme> =
  BASE_COLORS

// Neutral variant - only overrides LOW to use grey
export const SCORE_COLOR_VARS_NEUTRAL: Record<ScoreLevel, ScoreColorTheme> = {
  ...BASE_COLORS,
  [SCORE_LEVELS.LOW]: {
    bg: 'var(--score-low-neutral-bg)',
    textColor: 'var(--score-low-neutral-text)',
    border: 'var(--score-low-neutral-border)',
    progressColor: 'var(--score-low-neutral-progress)',
    progressBg: 'var(--score-low-neutral-progress-bg)',
  },
} as const

/**
 * Gets the score level directly from a score value
 * @param score - The score value to evaluate
 * @returns The corresponding score level
 */
export function getScoreLevel(score: number): ScoreLevel {
  if (score > SCORE_THRESHOLDS[SCORE_LEVELS.MID_HIGH]) return SCORE_LEVELS.HIGH
  if (score > SCORE_THRESHOLDS[SCORE_LEVELS.MEDIUM])
    return SCORE_LEVELS.MID_HIGH
  if (score > SCORE_THRESHOLDS[SCORE_LEVELS.MID_LOW]) return SCORE_LEVELS.MEDIUM
  if (score > SCORE_THRESHOLDS[SCORE_LEVELS.LOW]) return SCORE_LEVELS.MID_LOW
  return SCORE_LEVELS.LOW
}

/**
 * Checks if the current level is higher than the previous level
 * @param current - The current level to check
 * @param previous - The previous level to compare against
 * @returns True if the current level is higher than the previous level
 */
export function isHigherLevel(
  current: ScoreLevel,
  previous: ScoreLevel,
): boolean {
  const currentIndex = SCORE_LEVEL_ORDER.indexOf(current)
  const previousIndex = SCORE_LEVEL_ORDER.indexOf(previous)
  return currentIndex > previousIndex
}

export type ScoreColorVariant = 'vibrant' | 'neutral'

export const DEFAULT_SCORE_COLOR_VARIANT: ScoreColorVariant = 'vibrant'

const VARIANT_MAP: Record<
  ScoreColorVariant,
  Record<ScoreLevel, ScoreColorTheme>
> = {
  vibrant: SCORE_COLOR_VARS_VIBRANT,
  neutral: SCORE_COLOR_VARS_NEUTRAL,
}

/**
 * Gets the color variables for a given score
 * @param score - The score value to get colors for
 * @param variant - Color variant: 'vibrant' (red/orange for low scores) or 'neutral' (grey for low scores)
 * @returns Object containing color variables (bg, textColor, border, progressColor, progressBg)
 */
export function getScoreColors(
  score: number,
  variant: ScoreColorVariant = DEFAULT_SCORE_COLOR_VARIANT,
): ScoreColorTheme {
  const key = getScoreLevel(score)

  return VARIANT_MAP[variant][key]
}
